import * as React from 'react'
import './Pagination.scss'
import cx from 'classnames'

import { useTranslation } from 'react-i18next'

export interface IPaginationProps {
  pagination: {
    page: number
    page_size: number
    total_items: number
    total_pages: number
  }
  currentPage?: number
  paginationPerPage?: number
  pageChange: (page: number, currentPage: number) => void
  onChangePage: (page: number) => void
  style?: React.CSSProperties
}

const Pagination: React.FC<IPaginationProps> = ({
  pagination,
  currentPage,
  paginationPerPage,
  pageChange,
  onChangePage,
  style,
}: IPaginationProps) => {
  const { t } = useTranslation('translation')

  const getPageStart = (pageSize: number, pageNr: number) => {
    return pageSize * pageNr
  }

  /**
   * Label component for showing start and end number in pagination
   * @returns ()
   */
  const GetPageLabel = () => {
    const start = Math.max(
      getPageStart(pagination.page_size, currentPage - 1),
      0,
    )
    const end = Math.min(
      getPageStart(pagination.page_size, currentPage),
      pagination.total_items,
    )

    // return <>{`${start + 1} - ${end}`}</>
    return <>{`${pagination.total_items ? start + 1 : 0} - ${end}`}</>
  }

  /**
   * Page change handler for per page value
   * @param {React.ChangeEvent<HTMLSelectElement>} e
   */
  const pageChangeHandler = (e: React.ChangeEvent<HTMLSelectElement>) => {
    e.preventDefault()
    pageChange(+e.target.value, currentPage)
  }

  return (
    <div
      className="pagination-wraper d-flex flex-wrap justify-content-between align-items-center py-2"
      style={style}
    >
      {/* ------------------------ Rows per page ------------------------------ */}
      <nav className="d-flex align-items-center">
        <span>{t('Rows per page')}</span>
        <select onChange={pageChangeHandler}>
          {/*--------- Can be configured later ----- */}
          {[10, 15, 20, 25, 30, 50, 100].map((n: number, index: number) => {
            return (
              <option
                key={'opt__' + index}
                selected={paginationPerPage === n}
                value={n}
              >
                {n}
              </option>
            )
          })}
        </select>
        <div className="mx-4">
          <GetPageLabel /> {t('of')} {pagination.total_items}
        </div>
        <div>
          {/* ------------------------ Previous page ------------------------------ */}
          <button
            className={cx('btn first-page', {
              active: pagination.page > 1 && currentPage >= pagination.page,
            })}
            disabled={pagination.page === 1}
            onClick={() => onChangePage(1)}
          >
            <svg width="7.503" height="14" viewBox="0 0 7.503 14">
              <g
                id="Group_3649"
                data-name="Group 3649"
                transform="translate(-1156 -708)"
              >
                <path
                  data-name="Icon feather-chevron-down"
                  d="M0,0,5.5,5.5,11,0"
                  transform="translate(1162.796 709.25) rotate(90)"
                  fill="none"
                  stroke="rgba(9,35,230,0.5)"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1"
                />
                <line
                  id="Line_118"
                  data-name="Line 118"
                  y2="13"
                  transform="translate(1156.5 708.5)"
                  fill="none"
                  stroke="rgba(9,35,230,0.5)"
                  stroke-linecap="round"
                  stroke-width="1"
                />
              </g>
            </svg>
          </button>
          <button
            className={cx('btn prev-page', {
              active: currentPage > 1 && currentPage >= pagination.page,
            })}
            onClick={() => onChangePage(currentPage - 1)}
            disabled={pagination.page === 1}
          >
            <svg width="6.707" height="12.414" viewBox="0 0 6.707 12.414">
              <path
                data-name="Icon feather-chevron-down"
                d="M0,0,5.5,5.5,11,0"
                transform="translate(6 0.707) rotate(90)"
                fill="none"
                stroke="rgba(9,35,230,0.5)"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="1"
              />
            </svg>
          </button>
          {/* ------------------------ Next page ------------------------------ */}
          <button
            className={cx('btn next-page', {
              active: currentPage < pagination.total_pages,
            })}
            disabled={pagination.total_pages === currentPage}
            onClick={() => onChangePage(currentPage + 1)}
          >
            <svg width="6.707" height="12.414" viewBox="0 0 6.707 12.414">
              <path
                data-name="Icon feather-chevron-down"
                d="M9,19l5.5-5.5L20,19"
                transform="translate(19.707 -8.293) rotate(90)"
                fill="none"
                stroke="rgba(9,35,230,0.5)"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="1"
              />
            </svg>
          </button>
          <button
            className={cx('btn last-page', {
              active: currentPage < pagination.total_pages,
            })}
            disabled={pagination.total_pages === currentPage}
            onClick={() => onChangePage(pagination.total_pages)}
          >
            <svg width="7.411" height="14" viewBox="0 0 7.411 14">
              <g
                id="Group_3650"
                data-name="Group 3650"
                transform="translate(-1298.589 -708)"
              >
                <path
                  data-name="Icon feather-chevron-down"
                  d="M9,19l5.5-5.5L20,19"
                  transform="translate(1318.296 700.25) rotate(90)"
                  fill="none"
                  stroke="rgba(9,35,230,0.5)"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1"
                />
                <line
                  id="Line_117"
                  data-name="Line 117"
                  y2="13"
                  transform="translate(1305.5 708.5)"
                  fill="none"
                  stroke="rgba(9,35,230,0.5)"
                  stroke-linecap="round"
                  stroke-width="1"
                />
              </g>
            </svg>
          </button>
        </div>
      </nav>
    </div>
  )
}

export default Pagination
